import { Dispatch } from "redux";
import axios from "axios";
import { LoginScreen } from "@delorean/login";
import { PasswordResetScreen } from "@delorean/forgetPassword/types";
import {
  LOGIN_CHANNEL,
  QR_X_TYPE,
  RESEND_NOTIFY_X_TYPE,
} from "@delorean/_shared/constants";
import { encryptMoreTxi } from "@delorean/utils/encryptHash";
import { INVALID_AUTH_METHOD } from "@delorean/_shared/constants/serviceStatusCode";
import { getApiEndpoints, getEnvVar } from "@delorean/utils/env-utils";
import {
  ErrorType,
  ErrorTypeResetPsw,
} from "@delorean/_shared/domain/errorType";
import { RESEND_NOTIFY } from "./loginActions";

export const LOGIN_RESET_TEMP_PSW_REQUEST = "LOGIN_RESET_TEMP_PSW_REQUEST";
export const LOGIN_RESET_TEMP_PSW_SUCCESS = "LOGIN_RESET_TEMP_PSW_SUCCESS";
export const LOGIN_RESET_TEMP_PSW_FAILURE = "LOGIN_RESET_TEMP_PSW_FAILURE";
export const LOGIN_FORGET_PSW_REQUEST = "LOGIN_FORGET_PSW_REQUEST";
export const LOGIN_FORGET_PSW_SUCCESS = "LOGIN_FORGET_PSW_SUCCESS";
export const LOGIN_FORGET_PSW_FAILURE = "LOGIN_FORGET_PSW_FAILURE";
export const RESET_PSW_CANCEL_PROCEDURE = "RESET_PSW_CANCEL_PROCEDURE";
export const RESET_PSW_SESSION_EXPIRED = "RESET_PSW_SESSION_EXPIRED";
export const LOGIN_FORGET_PSW_ERROR_CHANNEL = "LOGIN_FORGET_PSW_ERROR_CHANNEL";
export const RESET_PSW_SUCCESS_QR_CODE = "RESET_PSW_SUCCESS_QR_CODE";

// RESET TEMPORARY PSW FLOW - this is called in reset password screen
export const performResetPassword = (newPassword: string, moreTxi: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_RESET_TEMP_PSW_REQUEST });
    const params = new URLSearchParams();
    const data: { [key: string]: any } = {
      newPassword: newPassword,
    };
    Object.keys(data).forEach((key) => {
      const value =
        typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key];
      params.append(key, value);
    });
    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);
    await axios
      .post(getApiEndpoints().RESET_PSW, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-client-risk": moreTxiBase64Encoded,
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_FAILURE,
          error: "Response error code",
        });
      })
      .catch((error) => {
        //ERROR === operation success on 409
        const response = error.response.data;
        if (
          error.response?.status === 409 &&
          response.mfaData.channel === LOGIN_CHANNEL.TOKEN
        ) {
          dispatch({
            type: LOGIN_RESET_TEMP_PSW_SUCCESS,
            payload: {
              screen: LoginScreen.PHYS_TOKEN,
              isAuthenticated: true,
              ...response,
            },
          });
        } else if (
          error.response?.status === 409 &&
          response.mfaData.channel === LOGIN_CHANNEL.APP_DEVICE
        ) {
          dispatch({
            type: LOGIN_RESET_TEMP_PSW_SUCCESS,
            payload: {
              screen: LoginScreen.APP_NOTIFICATION,
              isAuthenticated: true,
              ...response,
            },
          });
        } else {
          switch (error.response?.data?.errcode) {
            case ErrorType.sessionExpired:
              dispatch({
                type: RESET_PSW_SESSION_EXPIRED,
                error: error.response?.data?.errcode,
              });
              break;
            default:
              dispatch({
                type: LOGIN_RESET_TEMP_PSW_FAILURE,
                error: error.response?.data?.errcode,
              });
              break;
          }
        }
      });
  };
};

// FORGET PSW FLOW
export const performCheckIdentityForgetPassword = (
  contractCode: string,
  userCode: string,
  fiscalCode: string,
  captcha: string,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_FORGET_PSW_REQUEST });
    const params = new URLSearchParams();
    const data: { [key: string]: any } = {
      username: `${contractCode}_${userCode}`,
      fiscalCode: fiscalCode,
    };
    Object.keys(data).forEach((key) => {
      const value =
        typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key];
      params.append(key, value);
    });
    await axios
      .post(getApiEndpoints().FORGET_PSW, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-captcha-token": captcha,
          "x-captcha-kid": `${getEnvVar("VITE_CAPTCHA_KEY")}`,
          "x-captcha-type": "google",
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_FORGET_PSW_SUCCESS,
          payload: {
            screen: PasswordResetScreen.RESET_PASSWORD,
            isAuthenticated: true,
          },
        });
      })
      .catch((error) => {
        switch (error.response?.data?.errcode) {
          case ErrorTypeResetPsw.appNotEnrolledScaGeneration:
            dispatch({
              type: LOGIN_FORGET_PSW_ERROR_CHANNEL,
              error: error.response?.data?.errcode,
              payload: {
                screen: PasswordResetScreen.APP_NOT_ENROLLED,
              },
            });
            break;
          case ErrorType.sessionExpired:
            dispatch({
              type: RESET_PSW_SESSION_EXPIRED,
              error: error.response?.data?.errcode,
            });
            break;
          default:
            dispatch({
              type: LOGIN_FORGET_PSW_FAILURE,
              error: error.response?.data?.errcode,
            });
            break;
        }
      });
  };
};
export const performForgetPassword = (newPassword: string, moreTxi: string) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_RESET_TEMP_PSW_REQUEST });
    const params = new URLSearchParams();
    const data: { [key: string]: any } = {
      newPassword: newPassword,
    };
    Object.keys(data).forEach((key) => {
      const value =
        typeof data[key] === "object" ? JSON.stringify(data[key]) : data[key];
      params.append(key, value);
    });
    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);
    await axios
      .post(getApiEndpoints().RESET_PSW, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-client-risk": moreTxiBase64Encoded,
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_FAILURE,
          error: "Response error code",
        });
      })
      .catch((error) => {
        if (error.response?.status === 409) {
          dispatch({
            type: LOGIN_RESET_TEMP_PSW_SUCCESS,
            payload: {
              screen: PasswordResetScreen.APP_NOTIFICATION,
              isAuthenticated: true,
              ...error.response.data,
            },
          });
        } else {
          switch (error.response.data?.errcode) {
            case ErrorType.sessionExpired:
              dispatch({
                type: RESET_PSW_SESSION_EXPIRED,
                error: error.response?.data?.errcode,
              });
              break;
            default:
              dispatch({
                type: LOGIN_RESET_TEMP_PSW_FAILURE,
                error: error.response?.data?.errcode,
              });
              break;
          }
        }
      });
  };
};

//UTILS FOR BOTH FLOW______________________________________________________________________

//this is called in notification screen to resend notify to user
export const performPasswordResendNotify = (
  xOldTransaction: string,
  moreTxi: string,
) => {
  return async (dispatch: Dispatch) => {

    dispatch({ type: LOGIN_RESET_TEMP_PSW_REQUEST });
    const params = new URLSearchParams();
    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);
    await axios
      .post(getApiEndpoints().RESET_PSW, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-client-risk": moreTxiBase64Encoded,
          "x-type": RESEND_NOTIFY_X_TYPE,
          "x-old-transaction": xOldTransaction,
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_FAILURE,
          error: "Response error code",
        });
      })
      .catch((error) => {
        //ERROR === operation success on 409
        const response = error?.response?.data;
        if (error.response?.status === 409) {
          dispatch({
            type: RESEND_NOTIFY,
            payload: {
              ...error.response.data,
            },
          });
        } else {
          switch (error.response?.data?.errcode) {
            case ErrorType.sessionExpired:
              dispatch({
                type: RESET_PSW_SESSION_EXPIRED,
                error: error.response?.data?.errcode,
              });
              break;
            default:
              dispatch({
                type: LOGIN_RESET_TEMP_PSW_FAILURE,
                error: error.response?.data?.errcode,
              });
              break;
          }
        }
      });
  };
};
// this is called in notification screen to generate QR code
export const performResetForgetPasswordForQRCode = (
  moreTxi: string,
  xOldTransaction: string,
) => {
  return async (dispatch: Dispatch) => {
    dispatch({ type: LOGIN_RESET_TEMP_PSW_REQUEST });

    const params = new URLSearchParams();
    const moreTxiBase64Encoded = await encryptMoreTxi(moreTxi);
    await axios
      .post(getApiEndpoints().RESET_PSW, params, {
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "x-client-source": "WEB",
          "x-client-risk": moreTxiBase64Encoded,
          "x-type": QR_X_TYPE,
          "x-old-transaction": xOldTransaction,
        },
        withCredentials: true,
      })
      .then((response) => {
        dispatch({
          type: LOGIN_RESET_TEMP_PSW_FAILURE,
          error: "Response error code",
        });
      })
      .catch((error) => {
        //ERROR === operation success on 409
        if (error.response?.status === 409) {
          dispatch({
            type: RESET_PSW_SUCCESS_QR_CODE,
            payload: error.response.data,
          });
        } else {
          switch (error.response?.data?.errcode) {
            case ErrorType.sessionExpired:
              dispatch({
                type: RESET_PSW_SESSION_EXPIRED,
                error: error.response?.data?.errcode,
              });
              break;
            default:
              dispatch({
                type: LOGIN_RESET_TEMP_PSW_FAILURE,
                error: error.response?.data?.errcode,
              });
              break;
          }
        }
      });
  };
};

export const resetTempPassword = (
  screen:
    | PasswordResetScreen.RESET_PASSWORD_TEMP
    | LoginScreen.PHYS_TOKEN
    | LoginScreen.APP_QRCODE,
) => ({
  type: LOGIN_RESET_TEMP_PSW_SUCCESS,
  payload: { screen },
});

export const calcelResetPswProcedure = () => ({
  type: RESET_PSW_CANCEL_PROCEDURE,
  payload: { screen: PasswordResetScreen.CREDENTIAL_RESETPASSWORD },
});
